import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { type EnvironmentProviders, makeEnvironmentProviders } from '@angular/core';
import { BrnDialogService, provideBrnDialogDefaultOptions } from '@spartan-ng/brain/dialog';
import { StackAwareBrnDialogService } from './dialog-stack-guard';

/**
 * Provides default configuration for Spartan Helm components.
 *
 * This utility configures the Angular CDK overlay to disable the `usePopover`
 * behavior introduced in Angular 21, which causes CDK overlay-based components
 * (sheets, dialogs, tooltips, etc.) to render above `position: fixed` elements
 * like `<hlm-toaster>`.
 *
 * @returns {EnvironmentProviders} Environment providers to be added to the application config.
 *
 * @example
 * ```ts
 * // app.config.ts
 * import { provideSpartanHlm } from '@lite-workspace/shared/helm-ui/utils';
 *
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     provideSpartanHlm(),
 *     // ... other providers
 *   ],
 * };
 * ```
 */
export function provideSpartanHlm(): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: { usePopover: false },
    },
    // App-wide modal default: a click outside the panel dismisses it. Every dialog inherits this;
    // a dialog that must trap the user (unsaved-work guard) opts out with closeOnOutsidePointerEvents:false.
    provideBrnDialogDefaultOptions({ closeOnOutsidePointerEvents: true }),
    { provide: BrnDialogService, useClass: StackAwareBrnDialogService },
  ]);
}
